import BaseWidget, { escapeHtml } from './baseWidget.js';
import stammdatenService from '../../services/stammdatenService.js';
import basedataService from '../../services/basedataService.js';
import dayjs from '../../../../../shared/services/dayjs.js';
import { getAktuellerZeitraum } from '../../util.js';

const FILTER_TYPES = [
	{ value: 'all', label: 'Alle', title: 'Alle Fristen'},
	{ value: 'Probezeit', label: 'Probezeit', title: 'Probezeit Ende'},
	{ value: 'Befristung', label: 'Befristung', title: 'Befristung Ende'},
	{ value: 'Aufenthaltsgenehmigung', label: 'Aufenthaltsgenehmigung', title: 'Aufenthaltsgenehmigung Ende'},
	{ value: 'Arbeitserlaubnis', label: 'Arbeitserlaubnis', title: 'Arbeitserlaubnis Ende'},
	{ value: 'Immatrikulationsbescheinigung', label: 'Immatrikulation', title: 'Immatrikulationsbescheinigung Ende'},
	{ value: 'Kurzfristigkeit', label: 'Kurzfristigkeit', title: 'Kurzfristigkeit Ende'},
	{ value: 'InfektionsschutzErstbelehrung', label: 'IfSG Erstbelehrung', title: 'Infektionsschutz Erstbelehrung' },
	{ value: 'InfektionsschutzFolgebelehrung', label: 'IfSG Folgebelehrung', title: 'Infektionsschutz Folgebelehrung' },
	{ value: 'SBAusweis', label: 'SB-Ausweis', title: 'SB-Ausweis Ende' },
];

// Mapping Fristen-Typ → aria-label des zugehörigen Input-Felds auf der Beschäftigung-Seite.
const TYPE_TO_FIELD = {
	Probezeit: 'ma-ProbezeitBis',
	Befristung: 'ma-BefristetBis',
	Aufenthaltsgenehmigung: 'ma-AufenthaltsgenehmigungBis',
	Arbeitserlaubnis: 'ma-ArbeitserlaubnisBis',
	Immatrikulationsbescheinigung: 'ma-Immatrikulationsbescheinigung',
	Kurzfristigkeit: 'ma-Personengruppenschluessel',
	InfektionsschutzErstbelehrung: 'ma-InfektionsschutzErstbelehrung',
	InfektionsschutzFolgebelehrung: 'ma-InfektionsschutzFolgebelehrung',
	SBAusweis: 'ma-SBAusweisBis',
};

// Ausgeschriebene Bezeichnung für die Listenanzeige (Filter-Chips nutzen die kürzere IfSG-Abkürzung).
const TYPE_LABELS = {
	InfektionsschutzErstbelehrung: 'Infektionsschutz Erstbelehrung',
	InfektionsschutzFolgebelehrung: 'Infektionsschutz Folgebelehrung',
	SBAusweis: 'SB-Ausweis',
};

/**
 * Ermittelt Anzeigetext und Dringlichkeit einer Frist – gemeinsam genutzt von
 * Widget-Liste und Druckansicht.
 */
function getDeadlineStatus({ daysRemaining, daysText: customDaysText, missing }) {
	const tageText = (n) => `${n} Tag${n === 1 ? '' : 'e'}`;
	if (missing) {
		return { urgencyClass: 'deadline-expired', daysText: 'fehlt', daysIcon: '<i class="bi bi-question-circle"></i> ' };
	}
	if (daysRemaining < 0) {
		return {
			urgencyClass: 'deadline-expired',
			daysText: customDaysText || `−${tageText(Math.abs(daysRemaining))}`,
			daysIcon: '<i class="bi bi-hourglass-bottom"></i> ',
		};
	}
	let urgencyClass = 'deadline-ok';
	if (daysRemaining < 7) urgencyClass = 'deadline-urgent';
	else if (daysRemaining < 30) urgencyClass = 'deadline-soon';
	return { urgencyClass, daysText: customDaysText || tageText(daysRemaining), daysIcon: '' };
}

/**
 * Listet bevorstehende Fristen und Ablaufdaten aller Mitarbeiter auf.
 * Zeigt: Mitarbeitername, Fristentyp, verbleibende Tage.
 */
export default class DeadlinesWidget extends BaseWidget {
	constructor(type, instanceId, state = {}) {
		super(type, instanceId, state);
		// Filter-Typ wird absichtlich nicht persistiert: wenn beim nächsten Laden keine
		// passenden Fristen vorhanden sind, würde das Widget leer wirken obwohl andere
		// Typen relevant wären. Beim Öffnen also immer "Alle" zeigen.
		this.filter = 'all';
		this.hideMissing = state.hideMissing === true;
		this.disabledTypes = new Set(Array.isArray(state.disabledTypes) ? state.disabledTypes : []);
		this._allDeadlines = [];
		this._allDeadlinesRaw = [];
	}

	serializeState() {
		return { hideMissing: this.hideMissing, disabledTypes: Array.from(this.disabledTypes) };
	}

	async onRender() {
		this.setLoading();
		const zeitraum = getAktuellerZeitraum();
		const [malist, laender] = await Promise.all([
			stammdatenService.holeMitarbeiterListe(
				stammdatenService.aktuelleBetriebsstaette,
				zeitraum,
			),
			basedataService.holeLaenderBasedataProvider(),
		]);
		const euNationenschluessel = new Set(
			laender.filter((l) => l.EgMitglied == '1').map((l) => l.Nationenschluessel),
		);

		const heute = dayjs().startOf('day');
		const deadlines = [];

		// Sammle alle Fristen aller Mitarbeiter
		malist.forEach((ma) => {
			const austrittMA = ma.Austrittsdatum
				? dayjs(ma.Austrittsdatum)
				: null;

			// Ignoriere Mitarbeiter, die bereits ausgetreten sind
			if (austrittMA && heute.isAfter(austrittMA)) return;
			// Ignorie Mitarbeiter, die archiviert sind
			if (ma.Archiviert) return;

			const maName = `${ma.Nachname}, ${ma.Vorname}`;

			// Probezeitbis (nach Ablauf nicht mehr relevant)
			if (ma.ProbezeitBis) {
				const fristDatum = dayjs(ma.ProbezeitBis);
				if (fristDatum.isValid()) {
					const daysRemaining = heute.diff(fristDatum, 'days') * -1;
					if (daysRemaining >= 0 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							personalnummer: ma.Personalnummer,
							type: 'Probezeit',
							daysRemaining,
							fristDatum,
						});
					}
				}
			}

			// BefristetBis (befristeter Arbeitsvertrag)
			if (ma.BefristetBis) {
				const fristDatum = dayjs(ma.BefristetBis);
				if (fristDatum.isValid()) {
					const daysRemaining = heute.diff(fristDatum, 'days') * -1;
					if (daysRemaining >= -90 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							personalnummer: ma.Personalnummer,
							type: 'Befristung',
							daysRemaining,
							fristDatum,
						});
					}
				}
			}

			// Aufenthalts- und Arbeitsgenehmigung (nur für Nicht-EU-Bürger)
			if (ma.Staatsangehoerigkeit) {
				const isNonEU = !euNationenschluessel.has(ma.Staatsangehoerigkeit);
				if (isNonEU) {
					const aufenthaltFrist = ma.AufenthaltsgenehmigungBis ? dayjs(ma.AufenthaltsgenehmigungBis) : null;
					if (!aufenthaltFrist || !aufenthaltFrist.isValid()) {
						deadlines.push({
							maName,
							maId: ma._id,
							personalnummer: ma.Personalnummer,
							type: 'Aufenthaltsgenehmigung',
							missing: true,
						});
					} else {
						const daysRemaining = heute.diff(aufenthaltFrist, 'days') * -1;
						if (daysRemaining >= -90 && daysRemaining <= 90) {
							deadlines.push({
								maName,
								maId: ma._id,
								personalnummer: ma.Personalnummer,
								type: 'Aufenthaltsgenehmigung',
								daysRemaining,
								fristDatum: aufenthaltFrist,
							});
						}
					}

					const arbeitserlaubnisFrist = ma.ArbeitserlaubnisBis ? dayjs(ma.ArbeitserlaubnisBis) : null;
					if (!arbeitserlaubnisFrist || !arbeitserlaubnisFrist.isValid()) {
						deadlines.push({
							maName,
							maId: ma._id,
							personalnummer: ma.Personalnummer,
							type: 'Arbeitserlaubnis',
							missing: true,
						});
					} else {
						const daysRemaining = heute.diff(arbeitserlaubnisFrist, 'days') * -1;
						if (daysRemaining >= -90 && daysRemaining <= 90) {
							deadlines.push({
								maName,
								maId: ma._id,
								personalnummer: ma.Personalnummer,
								type: 'Arbeitserlaubnis',
								daysRemaining,
								fristDatum: arbeitserlaubnisFrist,
							});
						}
					}
				}
			}

			// Kurzfristige Beschäftigung (PGS 110): zwei Limits
			// - 70 Arbeitstage pro Kalenderjahr (Uebersicht.KurzfristigTage)
			// - max 10 Monate durchgehende Beschäftigung ab Eintritt
			// Beide werden separat angezeigt, damit klar ist welches Limit greift.
			if (ma.PGS === '110') {
				const kurzfristigTage = ma.KurzfristigTage;
				if (typeof kurzfristigTage === 'number') {
					const tageRest = 70 - kurzfristigTage;
					// Erst ab Annäherung an die Grenze (oder Überschreitung) anzeigen
					if (tageRest <= 30) {
						deadlines.push({
							maName,
							maId: ma._id,
							personalnummer: ma.Personalnummer,
							type: 'Kurzfristigkeit',
							subtype: '70-Tage-Grenze',
							daysRemaining: tageRest,
							daysText: `${kurzfristigTage} / 70 Tage`,
						});
					}
				}
				if (ma.Eintrittsdatum) {
					const grenze = dayjs(ma.Eintrittsdatum).add(10, 'months');
					if (grenze.isValid()) {
						const daysRemaining = grenze.diff(heute, 'days');
						if (daysRemaining <= 60) {
							deadlines.push({
								maName,
								maId: ma._id,
								personalnummer: ma.Personalnummer,
								type: 'Kurzfristigkeit',
								subtype: '10-Monats-Grenze',
								daysRemaining,
								fristDatum: grenze,
							});
						}
					}
				}
			}

			// Immatrikulationsbescheinigung (Studenten)
			if (ma.PGS === '106') {
				const immatrikulationFrist = ma.Immatrikulationsbescheinigung ? dayjs(ma.Immatrikulationsbescheinigung) : null;
				if (!immatrikulationFrist || !immatrikulationFrist.isValid()) {
					deadlines.push({
						maName,
						maId: ma._id,
						personalnummer: ma.Personalnummer,
						type: 'Immatrikulationsbescheinigung',
						missing: true,
					});
				} else {
					const daysRemaining = heute.diff(immatrikulationFrist, 'days') * -1;
					if (daysRemaining >= -90 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							personalnummer: ma.Personalnummer,
							type: 'Immatrikulationsbescheinigung',
							daysRemaining,
							fristDatum: immatrikulationFrist,
						});
					}
				}
			}

			// SB-Ausweis (nur bei befristetem Ausweis)
			if (ma.SBAusweis === 'befristet') {
				const sbAusweisFrist = ma.SBAusweisBis ? dayjs(ma.SBAusweisBis) : null;
				if (!sbAusweisFrist || !sbAusweisFrist.isValid()) {
					deadlines.push({
						maName,
						maId: ma._id,
						type: 'SBAusweis',
						missing: true,
					});
				} else {
					const daysRemaining = heute.diff(sbAusweisFrist, 'days') * -1;
					if (daysRemaining >= -90 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							type: 'SBAusweis',
							daysRemaining,
							fristDatum: sbAusweisFrist,
						});
					}
				}
			}

			// Infektionsschutzbelehrung: Erstbelehrung ist einmalig (nur Vorhandensein prüfen).
			// Die Folgebelehrung ist nur relevant, wenn die Erstbelehrung vorliegt – dann muss
			// sie aber ausgefüllt sein (sonst "fehlt") und gilt, sobald ausgefüllt, jeweils ein
			// Jahr ab ihrem Datum.
			const erstbelehrungFrist = ma.InfektionsschutzErstbelehrung ? dayjs(ma.InfektionsschutzErstbelehrung) : null;
			const hatErstbelehrung = erstbelehrungFrist && erstbelehrungFrist.isValid();
			if (!hatErstbelehrung) {
				deadlines.push({
					maName,
					maId: ma._id,
					personalnummer: ma.Personalnummer,
					type: 'InfektionsschutzErstbelehrung',
					missing: true,
				});
			} else {
				const folgebelehrungFrist = ma.InfektionsschutzFolgebelehrung ? dayjs(ma.InfektionsschutzFolgebelehrung) : null;
				if (!folgebelehrungFrist || !folgebelehrungFrist.isValid()) {
					deadlines.push({
						maName,
						maId: ma._id,
						personalnummer: ma.Personalnummer,
						type: 'InfektionsschutzFolgebelehrung',
						missing: true,
					});
				} else {
					const ablaufDatum = folgebelehrungFrist.add(1, 'year');
					const daysRemaining = heute.diff(ablaufDatum, 'days') * -1;
					if (daysRemaining >= -90 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							personalnummer: ma.Personalnummer,
							type: 'InfektionsschutzFolgebelehrung',
							daysRemaining,
							fristDatum: ablaufDatum,
						});
					}
				}
			}
		});

		// Sortiere: fehlende Daten zuerst, dann nach verbleibenden Tagen aufsteigend
		deadlines.sort((a, b) => {
			if (a.missing && !b.missing) return -1;
			if (!a.missing && b.missing) return 1;
			return a.daysRemaining - b.daysRemaining;
		});

		this._allDeadlinesRaw = deadlines;
		this._applyTypeFilter();

		this.bodyEl.innerHTML = `
			<div class="widget-deadline-filter"></div>
			<div class="widget-deadline-list"></div>
		`;
		this.renderFilterChips();
		this.renderList();
		this._installHeaderSettings();
	}

	/**
	 * Wendet die per Einstellungen-Menü deaktivierten Fristentypen auf die
	 * zuletzt geladenen Rohdaten an. Getrennt von onRender(), damit das
	 * Ein-/Ausblenden einzelner Checks ohne erneuten Server-Roundtrip geht.
	 */
	_applyTypeFilter() {
		this._allDeadlines = this._allDeadlinesRaw.filter((d) => !this.disabledTypes.has(d.type));
	}

	/**
	 * Hängt einen Einstellungen-Button in die Tile-Kopfzeile (neben Refresh/Close),
	 * über den einzelne Fristen-Checks dauerhaft ausgeblendet werden können – nötig,
	 * weil z.B. eine fehlende IfSG-Erstbelehrung sonst für jeden MA ohne Datum auftaucht
	 * und die Liste zumüllen kann. BaseWidget bietet bewusst keinen Header-Hook, deshalb
	 * DOM-Injection (siehe LohnkostenTrendWidget._installHeaderToggle für das Vorbild).
	 */
	_installHeaderSettings() {
		const header = this.bodyEl.closest('.dashboard-widget')?.querySelector('.dashboard-widget-header');
		if (!header) return;
		const actions = header.querySelector('.dashboard-widget-actions');
		if (!actions || actions.querySelector('.widget-deadline-settings-btn')) return;
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'widget-deadline-settings-btn';
		btn.title = 'Angezeigte Fristen auswählen';
		btn.setAttribute('aria-label', 'widget-deadline-settings');
		btn.innerHTML = '<i class="bi bi-sliders"></i>';
		btn.addEventListener('click', (e) => {
			e.stopPropagation();
			this._toggleSettingsPanel(btn);
		});
		actions.insertBefore(btn, actions.firstChild);

		const printBtn = document.createElement('button');
		printBtn.type = 'button';
		printBtn.className = 'widget-deadline-print-btn';
		printBtn.title = 'Fristen drucken';
		printBtn.setAttribute('aria-label', 'widget-deadline-print');
		printBtn.innerHTML = '<i class="bi bi-printer"></i>';
		printBtn.addEventListener('click', (e) => {
			e.stopPropagation();
			this._print();
		});
		actions.insertBefore(printBtn, btn.nextSibling);
	}

	/**
	 * Öffnet eine eigenständige Druckansicht der aktuell gefilterten Fristen in einem
	 * neuen Fenster (gedacht zur Übergabe an die Betriebsleitung). Bewusst nicht über
	 * @media print des Dashboards, da Grid/andere Widgets sonst aufwendig ausgeblendet
	 * werden müssten. Styles sind inline, damit die Seite unabhängig vom App-CSS ist.
	 */
	_print() {
		const win = window.open('', '_blank');
		if (!win) return;

		const deadlines = this._getVisibleDeadlines();
		const betrieb = stammdatenService.unternehmen?.Betriebsstaette
			?.find((bs) => bs._id === stammdatenService.aktuelleBetriebsstaette);
		const filterLabel = FILTER_TYPES.find((f) => f.value === this.filter)?.title || 'Alle Fristen';
		const filterInfo = this.hideMissing ? `${filterLabel} (ohne fehlende Angaben)` : filterLabel;

		const rows = deadlines.map((d) => {
			const { urgencyClass, daysText } = getDeadlineStatus(d);
			const typeLabel = TYPE_LABELS[d.type] || d.type;
			const typeLine = d.subtype ? `${typeLabel} · ${d.subtype}` : typeLabel;
			const status = !d.missing && d.daysRemaining < 0 ? `überfällig · ${daysText}` : daysText;
			return `
				<tr class="${urgencyClass}${d.missing ? ' missing' : ''}">
					<td class="ma">${escapeHtml(d.maName)}${d.personalnummer ? ` <span class="pnr">#${escapeHtml(d.personalnummer)}</span>` : ''}</td>
					<td>${escapeHtml(typeLine)}</td>
					<td class="datum">${d.fristDatum ? escapeHtml(d.fristDatum.format('DD.MM.YYYY')) : ''}</td>
					<td class="status">${escapeHtml(status)}</td>
					<td class="check"></td>
					<td class="notiz"></td>
				</tr>
			`;
		}).join('');

		win.document.write(`<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8">
<title>Fristen-Übersicht</title>
<style>
	@page { size: A4 portrait; margin: 15mm; }
	body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; margin: 0; }
	h1 { font-size: 16pt; margin: 0 0 4px; }
	.meta { margin-bottom: 12px; color: #333; }
	.meta div { margin: 1px 0; }
	table { width: 100%; border-collapse: collapse; }
	thead { display: table-header-group; }
	tr { page-break-inside: avoid; }
	th, td { border: 1px solid #999; padding: 4px 6px; text-align: left; vertical-align: top; }
	th { background: #eee; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
	/* Feste Breite für ~30 Zeichen: wird nicht von den fixen Spalten zusammengequetscht,
	   längere Namen brechen um statt die Tabelle zu sprengen */
	td.ma { width: 30ch; }
	/* Platz, um bei fehlendem Datum eins von Hand einzutragen (TT.MM.JJJJ) */
	td.datum { width: 25mm; white-space: nowrap; }
	.pnr { color: #666; font-size: 9pt; }
	tr.deadline-expired td.status, tr.deadline-urgent td.status { font-weight: bold; }
	tr.deadline-expired:not(.missing) td:first-child { border-left: 3px solid #000; }
	td.check { width: 14mm; }
	td.notiz { width: 45mm; }
	.empty { padding: 12px 0; }
</style>
</head>
<body>
	<h1>Fristen-Übersicht</h1>
	<div class="meta">
		${betrieb ? `<div>Betriebsstätte: ${escapeHtml(betrieb.BetriebsstaetteName || '')}</div>` : ''}
		<div>Stand: ${escapeHtml(dayjs().format('DD.MM.YYYY'))}</div>
		<div>Filter: ${escapeHtml(filterInfo)}</div>
	</div>
	${deadlines.length === 0
		? '<div class="empty">Keine bevorstehenden Fristen.</div>'
		: `<table>
		<thead>
			<tr><th>Mitarbeiter</th><th>Frist</th><th>Datum</th><th>Rest / Status</th><th>Erledigt</th><th>Notiz</th></tr>
		</thead>
		<tbody>${rows}</tbody>
	</table>`}
	<script>window.onload = () => window.print();</script>
</body>
</html>`);
		win.document.close();
	}

	_toggleSettingsPanel(anchorBtn) {
		if (this._settingsPanel) {
			this._closeSettingsPanel();
			return;
		}
		const panel = document.createElement('div');
		panel.className = 'widget-deadline-settings-panel';
		panel.innerHTML = `
			<div class="widget-deadline-settings-title">Angezeigte Fristen</div>
			${FILTER_TYPES.filter((f) => f.value !== 'all').map((f) => `
				<label class="widget-deadline-settings-row" title="${escapeHtml(f.title || f.label)}">
					<input type="checkbox" data-type="${escapeHtml(f.value)}" ${this.disabledTypes.has(f.value) ? '' : 'checked'}>
					<span>${escapeHtml(f.label)}</span>
				</label>
			`).join('')}
		`;
		document.body.appendChild(panel);
		const rect = anchorBtn.getBoundingClientRect();
		panel.style.top = `${rect.bottom + 4}px`;
		panel.style.right = `${window.innerWidth - rect.right}px`;
		panel.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
			checkbox.addEventListener('change', () => {
				const value = checkbox.dataset.type;
				if (checkbox.checked) {
					this.disabledTypes.delete(value);
				} else {
					this.disabledTypes.add(value);
				}
				this._applyTypeFilter();
				// Falls der aktuell gewählte Filter-Chip gerade ausgeblendet wurde, zurück auf "Alle".
				if (this.filter !== 'all' && this.disabledTypes.has(this.filter)) {
					this.filter = 'all';
				}
				this.renderFilterChips();
				this.renderList();
				if (window.myServices?.dashboard) {
					window.myServices.dashboard.saveConfig();
				}
			});
		});
		this._settingsPanel = panel;
		this._settingsOutsideHandler = (e) => {
			if (panel.contains(e.target) || anchorBtn.contains(e.target)) return;
			this._closeSettingsPanel();
		};
		this._settingsKeyHandler = (e) => {
			if (e.key === 'Escape') this._closeSettingsPanel();
		};
		document.addEventListener('mousedown', this._settingsOutsideHandler);
		document.addEventListener('keydown', this._settingsKeyHandler);
	}

	_closeSettingsPanel() {
		if (!this._settingsPanel) return;
		this._settingsPanel.remove();
		this._settingsPanel = null;
		document.removeEventListener('mousedown', this._settingsOutsideHandler);
		document.removeEventListener('keydown', this._settingsKeyHandler);
		this._settingsOutsideHandler = null;
		this._settingsKeyHandler = null;
	}

	destroy() {
		this._closeSettingsPanel();
	}

	renderFilterChips() {
		const filterEl = this.bodyEl.querySelector('.widget-deadline-filter');
		if (!filterEl) return;
		const availableTypes = new Set(this._allDeadlines.map((d) => d.type));
		const hasMissing = this._allDeadlines.some((d) => d.missing);
		const typeChips = FILTER_TYPES
			.filter((f) => f.value === 'all' || availableTypes.has(f.value))
			.sort((a, b) => {
				if (a.value === 'all') return -1;
				if (b.value === 'all') return 1;
				return a.label.localeCompare(b.label, 'de');
			})
			.map((f) => `
				<button type="button" class="deadline-chip${this.filter === f.value ? ' active' : ''}" data-filter="${escapeHtml(f.value)}"${f.title ? ` title="${escapeHtml(f.title)}"` : ''}>
					${escapeHtml(f.label)}
				</button>
			`).join('');
		// Toggle nur sichtbar wenn es überhaupt fehlende Werte gibt.
		// Das Fragezeichen-Icon spiegelt die "fehlt"-Pille in den Listeneinträgen,
		// daneben deutet das Augen-Icon an, ob diese gerade ein- oder ausgeblendet sind.
		const missingToggle = hasMissing
			? `<button type="button" class="deadline-chip deadline-chip-toggle${this.hideMissing ? ' active' : ''}" data-toggle="hideMissing" title="${this.hideMissing ? 'Fehlende Werte einblenden' : 'Fehlende Werte ausblenden'}" aria-label="Fehlende Werte ausblenden">
				<i class="bi ${this.hideMissing ? 'bi-eye-slash' : 'bi-eye'}"></i>
				<i class="bi bi-question-circle"></i>
			</button>`
			: '';
		filterEl.innerHTML = typeChips + missingToggle;
		filterEl.querySelectorAll('.deadline-chip:not(.deadline-chip-toggle)').forEach((chip) => {
			chip.addEventListener('click', () => {
				this.filter = chip.dataset.filter;
				filterEl.querySelectorAll('.deadline-chip:not(.deadline-chip-toggle)').forEach((c) => c.classList.toggle('active', c.dataset.filter === this.filter));
				this.renderList();
				// Persistiere ausgewählten Filter
				if (window.myServices?.dashboard) {
					window.myServices.dashboard.saveConfig();
				}
			});
		});
		const toggleEl = filterEl.querySelector('.deadline-chip-toggle');
		if (toggleEl) {
			toggleEl.addEventListener('click', () => {
				this.hideMissing = !this.hideMissing;
				this.renderFilterChips();
				this.renderList();
				if (window.myServices?.dashboard) {
					window.myServices.dashboard.saveConfig();
				}
			});
		}
	}

	/** Fristen nach aktivem Filter-Chip und "Fehlende ausblenden" – Basis für Liste und Druck. */
	_getVisibleDeadlines() {
		let filtered = this.filter === 'all'
			? this._allDeadlines
			: this._allDeadlines.filter((d) => d.type === this.filter);
		if (this.hideMissing) {
			filtered = filtered.filter((d) => !d.missing);
		}
		return filtered;
	}

	renderList() {
		const listEl = this.bodyEl.querySelector('.widget-deadline-list');
		if (!listEl) return;

		const filtered = this._getVisibleDeadlines();

		if (filtered.length === 0) {
			listEl.innerHTML = '<div class="widget-empty">Keine bevorstehenden Fristen.</div>';
			return;
		}

		listEl.innerHTML = '';
		filtered.forEach((deadline) => {
			const { maName, maId, personalnummer, type, subtype } = deadline;
			const item = document.createElement('div');
			item.className = 'widget-deadline-item';
			item.addEventListener('click', async () => {
				await window.myHandlers.selectMA(maId, 'Beschäftigung');
				const fieldLabel = TYPE_TO_FIELD[type];
				if (fieldLabel) {
					const input = document.querySelector(`[aria-label="${fieldLabel}"]`);
					if (input) {
						input.scrollIntoView({ behavior: 'smooth', block: 'center' });
						input.classList.add('field-highlight');
						setTimeout(() => input.classList.remove('field-highlight'), 3000);
					}
				}
			});

			const { urgencyClass, daysText, daysIcon } = getDeadlineStatus(deadline);

			const typeLabel = TYPE_LABELS[type] || type;
			const typeLine = subtype ? `${typeLabel} · ${subtype}` : typeLabel;
			item.innerHTML = `
				<div class="deadline-content">
					<div class="deadline-ma">${escapeHtml(maName)}${personalnummer ? ` <span class="deadline-pnr">#${escapeHtml(personalnummer)}</span>` : ''}</div>
					<div class="deadline-type">${escapeHtml(typeLine)}</div>
				</div>
				<div class="deadline-days ${urgencyClass}">${daysIcon}${escapeHtml(daysText)}</div>
			`;
			listEl.appendChild(item);
		});
	}
}
